import { useQuery } from "@tanstack/react-query";
import { Link, useLocation, useNavigate } from "@tanstack/react-router";
import {
  BookOpen,
  FileText,
  FolderOpen,
  Hash,
  Highlighter,
  Home,
  Inbox,
  MessageSquare,
  Network,
  Plus,
  Search,
  Settings,
  Sparkles,
  Upload,
} from "lucide-react";
import { type ReactNode, useState } from "react";
import { useRecentItems } from "../../hooks/useRecentItems";
import { api } from "../../lib/api";
import { cn } from "../../lib/utils";
import { useUI } from "../../stores/ui";
import { type SectionItem, SectionWithSearch } from "./SectionWithSearch";

interface ItemProps {
  icon?: ReactNode;
  label: string;
  to?: string;
  href?: string;
  count?: number;
  active?: boolean;
  indent?: number;
  onClick?: () => void;
}

function Item({ icon, label, to, href, count, active, indent = 0, onClick }: ItemProps) {
  const content = (
    <>
      <span className="grid w-5 place-items-center text-(--text-tertiary)">{icon}</span>
      <span className="flex-1 truncate">{label}</span>
      {count != null && (
        <span className="text-[10px] font-mono text-(--text-tertiary)">{count}</span>
      )}
    </>
  );
  const className = cn(
    "flex items-center gap-2 rounded-md px-2 py-1.5 text-sm transition-colors no-underline",
    active
      ? "bg-(--bg-active) text-(--text-primary) font-medium"
      : "text-(--text-secondary) hover:bg-(--bg-hover)",
  );
  if (to) {
    return (
      <Link to={to} className={className} style={{ paddingLeft: 8 + indent * 12 }}>
        {content}
      </Link>
    );
  }
  if (href) {
    return (
      <a href={href} className={className} style={{ paddingLeft: 8 + indent * 12 }}>
        {content}
      </a>
    );
  }
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(className, "w-full text-left")}
      style={{ paddingLeft: 8 + indent * 12 }}
    >
      {content}
    </button>
  );
}

function PrimarySection({ children }: { children: ReactNode }) {
  return <div className="flex flex-col gap-0.5 px-2">{children}</div>;
}

export function Sidebar() {
  const collapsed = useUI((s) => s.sidebarCollapsed);
  const loc = useLocation();
  const navigate = useNavigate();
  const path = loc.pathname;
  const docsQuery = useQuery({
    queryKey: ["sidebar-docs"],
    queryFn: () => api.listDocs({ limit: 100 }),
    staleTime: 30_000,
  });
  const notebooksQuery = useQuery({
    queryKey: ["notebooks"],
    queryFn: () => api.listNotebooks(),
    staleTime: 30_000,
  });
  // B5.1 — real collections (with pinned + folder_path) drive the sidebar
  const collectionsQuery = useQuery({
    queryKey: ["collections", { sidebar: true }],
    queryFn: () => api.listCollections({}),
    staleTime: 30_000,
  });

  const recentProjects = useRecentItems("collections");
  const recentTags = useRecentItems("tags");
  const recentNotebooks = useRecentItems("notebooks");

  const docs = docsQuery.data?.items ?? [];

  // B5.1 — split collections into Pinned vs Folder tree (folder_path).
  const collections = collectionsQuery.data?.items ?? [];
  const pinnedCollections = collections.filter((c) => c.pinned === 1);
  const unpinned = collections.filter((c) => c.pinned !== 1);
  const folderMap = new Map<string | null, typeof collections>();
  for (const col of unpinned) {
    const key = col.folder_path?.trim() || null;
    const arr = folderMap.get(key) ?? [];
    arr.push(col);
    folderMap.set(key, arr);
  }
  const folders: { key: string | null; items: typeof collections }[] = Array.from(
    folderMap.entries(),
  )
    .map(([key, items]) => ({ key, items }))
    .sort((a, b) => {
      if (a.key === null) return 1;
      if (b.key === null) return -1;
      return a.key.localeCompare(b.key);
    });

  const tagItems: SectionItem[] = Array.from(
    docs.reduce((counts, doc) => {
      for (const tag of doc.tags) counts.set(tag, (counts.get(tag) ?? 0) + 1);
      return counts;
    }, new Map<string, number>()),
  )
    .map(([tag, count]) => ({ id: tag, label: tag, count }))
    .sort((a, b) => (b.count ?? 0) - (a.count ?? 0) || a.label.localeCompare(b.label));

  const notebookItems: SectionItem[] = (notebooksQuery.data?.items ?? []).map((nb) => ({
    id: nb.id,
    label: nb.title,
    updatedAt: nb.updated_at,
  }));

  if (collapsed) {
    return (
      <aside
        data-testid="sidebar"
        data-collapsed="true"
        className="hidden w-14 flex-col items-center gap-2 border-r border-(--border-subtle) bg-(--bg-elevated) py-3 md:flex"
      >
        <Link to="/" className="rounded-md p-2 hover:bg-(--bg-hover)" aria-label="Inicio">
          <Home size={18} />
        </Link>
        <Link to="/docs" className="rounded-md p-2 hover:bg-(--bg-hover)" aria-label="Documentos">
          <FileText size={18} />
        </Link>
        <Link
          to="/collections"
          className="rounded-md p-2 hover:bg-(--bg-hover)"
          aria-label="Colecciones"
        >
          <FolderOpen size={18} />
        </Link>
        <Link to="/search" className="rounded-md p-2 hover:bg-(--bg-hover)" aria-label="Buscar">
          <Search size={18} />
        </Link>
        <Link to="/chat" className="rounded-md p-2 hover:bg-(--bg-hover)" aria-label="Chat">
          <MessageSquare size={18} />
        </Link>
        <Link to="/notebooks" className="rounded-md p-2 hover:bg-(--bg-hover)" aria-label="Apuntes">
          <BookOpen size={18} />
        </Link>
        <a href="/graph" className="rounded-md p-2 hover:bg-(--bg-hover)" aria-label="Graph">
          <Network size={18} />
        </a>
        <Link to="/upload" className="rounded-md p-2 hover:bg-(--bg-hover)" aria-label="Importar">
          <Upload size={18} />
        </Link>
      </aside>
    );
  }

  return (
    <aside
      data-testid="sidebar"
      data-collapsed="false"
      className="hidden w-60 flex-col gap-2 overflow-y-auto border-r border-(--border-subtle) bg-(--bg-elevated) py-2 md:flex"
    >
      <div className="px-2">
        <Link
          to="/upload"
          data-testid="sidebar-create"
          className="flex w-full items-center justify-center gap-2 rounded-md bg-(--accent-500) px-3 py-1.5 text-sm font-medium text-white shadow-sm transition hover:bg-(--accent-600)"
        >
          <Plus size={14} /> Crear
        </Link>
      </div>

      <PrimarySection>
        <Item icon={<Home size={15} />} label="Inicio" to="/" active={path === "/"} />
        <Item
          icon={<FileText size={15} />}
          label="Documentos"
          to="/docs"
          active={path.startsWith("/docs")}
        />
        <Item
          icon={<FolderOpen size={15} />}
          label="Colecciones"
          to="/collections"
          active={path.startsWith("/collections")}
        />
        <Item
          icon={<Search size={15} />}
          label="Buscar"
          to="/search"
          active={path.startsWith("/search")}
        />
        <Item
          icon={<MessageSquare size={15} />}
          label="Chat"
          to="/chat"
          active={path.startsWith("/chat")}
        />
        <Item
          icon={<BookOpen size={15} />}
          label="Apuntes"
          to="/notebooks"
          active={path.startsWith("/notebooks")}
        />
        <Item
          icon={<Highlighter size={15} />}
          label="Highlights"
          href="/highlights"
          active={path.startsWith("/highlights")}
        />
        <Item
          icon={<Network size={15} />}
          label="Graph"
          href="/graph"
          active={path.startsWith("/graph")}
        />
        <Item
          icon={<Upload size={15} />}
          label="Importar"
          to="/upload"
          active={path.startsWith("/upload")}
        />
      </PrimarySection>

      <CollectionsSection
        pinned={pinnedCollections}
        folders={folders}
        currentSlug={path.match(/^\/collections\/([^/]+)/)?.[1]}
        onNavigate={(slug) => {
          recentProjects.record(slug);
          void navigate({ to: "/collections/$slug", params: { slug } });
        }}
      />
      {/* C5 — legacy "Proyectos (docs)" retirado: las colecciones cubren ya
          la agrupación tras la migración 0012b. Si se necesita filtrar docs
          por proyecto se hace desde /docs?project=. */}

      <SectionWithSearch
        title="Mis apuntes"
        items={notebookItems}
        recentIds={recentNotebooks.items}
        seeAllHref="/notebooks"
        storageKey="sidebar-notebooks"
        emptyLabel="Sin notebooks"
        renderItem={(item) => (
          <Item
            icon={<BookOpen size={14} />}
            label={item.label}
            href={`/notebooks/${item.id}`}
            active={path === `/notebooks/${item.id}`}
            onClick={() => recentNotebooks.record(item.id)}
          />
        )}
      />

      <SectionWithSearch
        title="Etiquetas"
        items={tagItems}
        recentIds={recentTags.items}
        seeAllHref="/search"
        storageKey="sidebar-tags"
        emptyLabel="Sin etiquetas"
        renderItem={(item) => (
          <Item
            icon={<Hash size={14} />}
            label={item.label}
            count={item.count}
            href="/search"
            onClick={() => recentTags.record(item.id)}
          />
        )}
      />

      <div className="px-2">
        <div className="px-2 pb-1 pt-3 text-[10px] font-semibold uppercase tracking-wider text-(--text-tertiary)">
          Admin
        </div>
        <div className="flex flex-col gap-0.5">
          <Item icon={<Inbox size={14} />} label="Ingest jobs" to="/admin/ingest" />
          <Item icon={<Sparkles size={14} />} label="Autolinks" to="/admin/autolinks" />
          <Item icon={<Settings size={14} />} label="Ajustes" to="/settings" />
        </div>
      </div>
    </aside>
  );
}

/**
 * B5.1 — Collections sidebar group with Pinned section + folder-path tree.
 *
 * Datos vienen de /v2/collections (con campos `pinned` + `folder_path`
 * añadidos en la migración 0016). Pinned arriba con su dot; el resto se
 * agrupa por folder (collapsable, recordando el estado en localStorage).
 */
function CollectionsSection({
  pinned,
  folders,
  currentSlug,
  onNavigate,
}: {
  pinned: import("../../lib/api").CollectionSummary[];
  folders: { key: string | null; items: import("../../lib/api").CollectionSummary[] }[];
  currentSlug: string | undefined;
  onNavigate: (slug: string) => void;
}) {
  return (
    <div className="px-2" data-testid="sidebar-collections">
      <div className="flex items-baseline justify-between px-2 pb-1 pt-3">
        <span className="text-[10px] font-semibold uppercase tracking-wider text-(--text-tertiary)">
          Colecciones
        </span>
        <a
          href="/collections"
          className="text-[10px] font-mono text-(--text-tertiary) hover:text-(--text-primary)"
        >
          ver todas →
        </a>
      </div>

      {pinned.length > 0 && (
        <div className="mb-1.5" data-testid="sidebar-pinned">
          <div className="px-2 pb-0.5 text-[9px] font-mono uppercase tracking-wider text-(--text-tertiary)">
            Fijadas
          </div>
          <ul className="flex flex-col gap-0.5">
            {pinned.map((col) => (
              <CollectionRow
                key={col.id}
                col={col}
                active={currentSlug === col.slug}
                onNavigate={onNavigate}
                showPin
              />
            ))}
          </ul>
        </div>
      )}

      {folders.map((f) => (
        <FolderGroup
          key={f.key ?? "_root"}
          name={f.key}
          items={f.items}
          currentSlug={currentSlug}
          onNavigate={onNavigate}
        />
      ))}

      {pinned.length === 0 && folders.every((f) => f.items.length === 0) && (
        <p className="px-2 py-2 text-xs text-(--text-tertiary)">Sin colecciones aún</p>
      )}
    </div>
  );
}

function FolderGroup({
  name,
  items,
  currentSlug,
  onNavigate,
}: {
  name: string | null;
  items: import("../../lib/api").CollectionSummary[];
  currentSlug: string | undefined;
  onNavigate: (slug: string) => void;
}) {
  const key = `sidebar-folder-${name ?? "_root"}`;
  const [open, setOpen] = useFolderOpen(key, true);
  if (items.length === 0) return null;
  const label = name ?? "Sin carpeta";
  return (
    <div className="mb-1" data-testid="sidebar-folder" data-folder={name ?? ""}>
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="flex w-full items-center gap-1 rounded px-2 py-0.5 text-[10px] font-mono uppercase tracking-wider text-(--text-tertiary) hover:bg-(--bg-hover)"
        data-testid="sidebar-folder-toggle"
      >
        <span
          aria-hidden="true"
          style={{
            display: "inline-block",
            transform: open ? "rotate(90deg)" : "rotate(0deg)",
            transition: "transform 120ms",
          }}
        >
          ▶
        </span>
        <span>{label}</span>
        <span className="ml-auto font-mono">{items.length}</span>
      </button>
      {open && (
        <ul className="mt-0.5 flex flex-col gap-0.5">
          {items.map((col) => (
            <CollectionRow
              key={col.id}
              col={col}
              active={currentSlug === col.slug}
              onNavigate={onNavigate}
            />
          ))}
        </ul>
      )}
    </div>
  );
}

function CollectionRow({
  col,
  active,
  onNavigate,
  showPin,
}: {
  col: import("../../lib/api").CollectionSummary;
  active: boolean;
  onNavigate: (slug: string) => void;
  showPin?: boolean;
}) {
  return (
    <li>
      <button
        type="button"
        onClick={() => onNavigate(col.slug)}
        data-testid="sidebar-collection"
        data-collection-id={col.id}
        data-active={active ? "true" : "false"}
        className={`flex w-full items-center gap-2 rounded-md px-2 py-1 text-sm transition ${
          active
            ? "bg-(--bg-active) text-(--text-primary) font-medium"
            : "text-(--text-secondary) hover:bg-(--bg-hover)"
        }`}
      >
        <FolderOpen size={14} className="text-(--text-tertiary)" />
        <span className="flex-1 truncate text-left">{col.title}</span>
        {showPin && (
          <span
            data-testid="sidebar-collection-pin"
            aria-hidden="true"
            className="inline-block h-1.5 w-1.5 rounded-full bg-(--accent-500)"
          />
        )}
        {col.item_count > 0 && (
          <span className="font-mono text-[10px] text-(--text-tertiary)">{col.item_count}</span>
        )}
      </button>
    </li>
  );
}

function useFolderOpen(key: string, defaultOpen: boolean): [boolean, (v: boolean) => void] {
  const initial = (() => {
    try {
      const raw = window.localStorage.getItem(key);
      if (raw === "open") return true;
      if (raw === "closed") return false;
    } catch {}
    return defaultOpen;
  })();
  const [open, setOpenState] = useState(initial);
  const set = (v: boolean) => {
    setOpenState(v);
    try {
      window.localStorage.setItem(key, v ? "open" : "closed");
    } catch {}
  };
  return [open, set];
}
